import { InspectionRecord } from '../services/localBackend';
import { getDefectTypeLabel, getSeverityLabel } from '../hooks/useQueries';

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

export async function generateInspectionPDF(record: InspectionRecord): Promise<void> {
  // imageReference is now a base64 data URL or empty string
  const imgDataUrl = record.imageReference || '';

  const severityLabel = getSeverityLabel(record.finalClassification);
  const defectLabel = getDefectTypeLabel(record.defectType);
  const timestamp = formatTimestamp(record.timestamp);

  let severityColor = '#22c55e';
  if (record.finalClassification === 'majorDefect' as any) severityColor = '#ef4444';
  else if (record.finalClassification === 'minorDefect' as any) severityColor = '#f59e0b';

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>VisionInspect Report - ${record.componentId}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Inter', Arial, sans-serif;
          background: #0f1117;
          color: #e2e8f0;
          padding: 40px;
          min-height: 100vh;
        }
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 2px solid #1e40af;
          padding-bottom: 20px;
          margin-bottom: 30px;
        }
        .logo-area h1 {
          font-size: 24px;
          font-weight: 800;
          color: #3b82f6;
          letter-spacing: 2px;
        }
        .logo-area p {
          font-size: 11px;
          color: #64748b;
          letter-spacing: 1px;
          text-transform: uppercase;
        }
        .report-id {
          text-align: right;
          font-size: 12px;
          color: #64748b;
        }
        .report-id strong {
          display: block;
          font-size: 18px;
          color: #3b82f6;
          font-family: monospace;
        }
        .section {
          background: #1a1f2e;
          border: 1px solid #2d3748;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }
        .section-title {
          font-size: 11px;
          font-weight: 700;
          color: #3b82f6;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid #2d3748;
        }
        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .field {
          margin-bottom: 12px;
        }
        .field-label {
          font-size: 10px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 4px;
        }
        .field-value {
          font-size: 15px;
          font-weight: 600;
          color: #e2e8f0;
          font-family: monospace;
        }
        .severity-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 700;
          background: ${severityColor}22;
          color: ${severityColor};
          border: 1px solid ${severityColor}44;
        }
        .score-bar {
          height: 8px;
          background: #2d3748;
          border-radius: 4px;
          margin-top: 6px;
          overflow: hidden;
        }
        .score-fill {
          height: 100%;
          background: ${severityColor};
          border-radius: 4px;
          width: ${record.severityScore}%;
        }
        .image-container {
          text-align: center;
        }
        .image-container img {
          max-width: 100%;
          max-height: 300px;
          border-radius: 6px;
          border: 1px solid #2d3748;
        }
        .no-image {
          height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0f1117;
          border: 1px dashed #2d3748;
          border-radius: 6px;
          color: #64748b;
          font-size: 13px;
        }
        .footer {
          margin-top: 30px;
          padding-top: 16px;
          border-top: 1px solid #2d3748;
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #475569;
        }
        @media print {
          body { background: white; color: #1a1a1a; }
          .section { background: #f8fafc; border-color: #e2e8f0; }
          .field-value { color: #1a1a1a; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo-area">
          <h1>VISIONINSPECT</h1>
          <p>AI Inspection System — Inspection Report</p>
        </div>
        <div class="report-id">
          <span>Component ID</span>
          <strong>${record.componentId}</strong>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Inspection Details</div>
        <div class="grid-2">
          <div class="field">
            <div class="field-label">Component ID</div>
            <div class="field-value">${record.componentId}</div>
          </div>
          <div class="field">
            <div class="field-label">Timestamp</div>
            <div class="field-value">${timestamp}</div>
          </div>
          <div class="field">
            <div class="field-label">Detected Defect Type</div>
            <div class="field-value">${defectLabel}</div>
          </div>
          <div class="field">
            <div class="field-label">Final Classification</div>
            <div class="field-value">
              <span class="severity-badge">${severityLabel}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">AI Analysis Results</div>
        <div class="grid-2">
          <div class="field">
            <div class="field-label">Severity Score</div>
            <div class="field-value">${record.severityScore}/100</div>
            <div class="score-bar"><div class="score-fill"></div></div>
          </div>
          <div class="field">
            <div class="field-label">Model Confidence</div>
            <div class="field-value">${record.modelConfidence}%</div>
            <div class="score-bar">
              <div class="score-fill" style="width: ${record.modelConfidence}%; background: #3b82f6;"></div>
            </div>
          </div>
        </div>
      </div>

      ${imgDataUrl ? `
      <div class="section">
        <div class="section-title">Inspection Image</div>
        <div class="image-container">
          <img src="${imgDataUrl}" alt="Inspection Image" />
        </div>
      </div>
      ` : `
      <div class="section">
        <div class="section-title">Inspection Image</div>
        <div class="no-image">Image not available</div>
      </div>
      `}

      <div class="footer">
        <span>Generated by VisionInspect AI Inspection System</span>
        <span>${new Date().toLocaleString()}</span>
      </div>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    alert('Please allow popups to download the PDF report.');
    return;
  }

  printWindow.document.write(htmlContent);
  printWindow.document.close();

  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };
}
